import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../context/ToastContext';
import { useDirectModule1ToModule2 } from '../../services/module2Service';
import ExportResultsModal from './ExportResultsModal';

// Define the ref type to expose methods to parent
export interface Module2IntegrationRef {
  handleExport: () => Promise<void>;
}

interface Module2IntegrationProps {
  workflowId: string;
  clientId?: string;
  analysisResults: any;
  uploadedFilePath?: string | null;
  isServiceAvailable: boolean;
  setIsClassifying: (value: boolean) => void;
  setClassificationResult: (result: any) => void;
  isClassifying: boolean;
  classificationResult: any;
  isResultModalOpen: boolean;
  setIsResultModalOpen: (isOpen: boolean) => void;
}

const Module2Integration = forwardRef<Module2IntegrationRef, Module2IntegrationProps>(({ 
  workflowId, 
  clientId, 
  analysisResults, 
  uploadedFilePath, 
  isServiceAvailable,
  setIsClassifying,
  setClassificationResult,
  isClassifying,
  classificationResult,
  isResultModalOpen,
  setIsResultModalOpen
}, ref) => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const directModule1ToModule2Mutation = useDirectModule1ToModule2();

  const readKeywordsFromUploadedFile = async (filePath: string): Promise<string[]> => {
    try {
      console.log(`Starting to read keywords from uploaded file: ${filePath}`);
      
      const blobService = (await import('../../services/storage/blobService')).default;
      const fileContent = await blobService.getFileContent(filePath);
      
      if (!fileContent) {
        console.error("File content is empty");
        throw new Error("Could not retrieve file content - empty response");
      }
      
      console.log(`Retrieved file content (${fileContent.length} bytes)`);
      
      const fileExtension = filePath.split('.').pop()?.toLowerCase();
      let keywordsList: string[] = [];
      
      if (fileExtension === 'csv') {
        if (fileContent.includes(',')) {
          keywordsList = fileContent.split(/[\r\n]+/)
            .map(line => line.split(',')[0])
            .filter(keyword => keyword && keyword.trim());
        } else {
          keywordsList = fileContent.split(/[\r\n]+/)
            .filter(keyword => keyword && keyword.trim());
        }
        console.log(`Parsed CSV content: ${keywordsList.length} keywords`);
      } else if (fileExtension === 'txt') {
        keywordsList = fileContent.split(/[\r\n]+/)
          .filter(keyword => keyword && keyword.trim());
        console.log(`Parsed TXT content: ${keywordsList.length} keywords`);
      } else {
        keywordsList = fileContent.split(/[\r\n]+/)
          .filter(keyword => keyword && keyword.trim());
        console.log(`Parsed unknown file type content: ${keywordsList.length} keywords`);
      }
      
      if (keywordsList.length === 0) {
        console.error("No keywords extracted from file content");
        throw new Error("File was parsed successfully but no keywords were found");
      }
      
      console.log(`First 5 keywords: ${keywordsList.slice(0, 5).join(', ')}`);
      
      return keywordsList;
    } catch (error) {
      console.error("Error reading keywords from uploaded file:", error);
      throw error;
    }
  };

  const loadTestKeywords = async (): Promise<string[]> => {
    try {
      // First try to extract keywords from analysis results
      if (analysisResults?.topics && Array.isArray(analysisResults.topics)) {
        const extractedKeywords: string[] = [];
        
        analysisResults.topics.forEach((topic: any) => {
          if (topic.subtopics && Array.isArray(topic.subtopics)) {
            topic.subtopics.forEach((subtopic: any) => {
              if (subtopic.keywords && Array.isArray(subtopic.keywords)) {
                extractedKeywords.push(...subtopic.keywords);
              }
            });
          }
        });
        
        if (extractedKeywords.length > 0) {
          console.log(`Extracted ${extractedKeywords.length} keywords from analysis results`);
          return extractedKeywords;
        }
      }
      
      console.warn('No keywords found in analysis results, using fallback automotive test data');
      const sampleKeywords = [
        "oil change near me",
        "firestone oil change coupon",
        "what type of oil does my car need",
        "why is my car leaking oil",
        "bmw x5 oil change cost",
        "oil light on dashboard",
        "synthetic vs conventional oil",
        "how often to change oil",
        "oil burning smell from engine",
        "low oil pressure warning light"
      ];
      return sampleKeywords;
    } catch (err) {
      console.error("Error loading keywords:", err);
      return ["oil change near me", "synthetic oil", "oil leak"];
    }
  };

  const handleExport = async () => {
    if (!analysisResults) {
      showToast('No analysis results available to send to Module 2', 'error');
      return;
    }
    
    try {
      setIsClassifying(true);
      showToast('Sending analysis data directly to Module 2...', 'info');
      
      console.log('Full analysis results structure:', analysisResults);
      
      let keywords: string[] = [];
      
      // Priority 1: Try to use keywords from uploaded file
      if (uploadedFilePath) {
        try {
          keywords = await readKeywordsFromUploadedFile(uploadedFilePath);
          console.log(`Successfully read ${keywords.length} keywords from ${uploadedFilePath}`);
          showToast(`Read ${keywords.length} keywords from uploaded file`, 'info');
        } catch (error) {
          console.error("Error reading keywords from file:", error);
          const errorMessage = error instanceof Error 
            ? error.message 
            : "Unknown error reading keywords";
          showToast(`Could not read keywords from uploaded file: ${errorMessage}. Will try to use keywords from analysis results.`, "warning");
        }
      }
      
      // Priority 2: If no uploaded file keywords, try to extract from analysis results
      if (keywords.length === 0) {
        keywords = await loadTestKeywords();
        if (keywords.length > 0 && analysisResults?.topics) {
          console.log(`Using ${keywords.length} keywords extracted from analysis results`);
          showToast(`Using ${keywords.length} keywords from analysis results`, 'info');
        } else {
          console.log('No analysis keywords available, using fallback test data');
          showToast('No keywords found in analysis results. Using sample keywords instead.', 'warning');
        }
      }
      
      if (keywords.length === 0) {
        showToast('No keywords available for classification. Please upload a valid keywords file.', 'error');
        setIsClassifying(false);
        return;
      }
      
      const result = await directModule1ToModule2Mutation.mutateAsync({
        module1Results: analysisResults,
        workflowId: workflowId,
        clientId: clientId,
        keywords: (function() {
          try {
            const storedKeywords = sessionStorage.getItem('latest_keywords');
            if (storedKeywords) {
              const parsedKeywords = JSON.parse(storedKeywords);
              if (Array.isArray(parsedKeywords) && parsedKeywords.length > 0) {
                console.log(`Using ${parsedKeywords.length} keywords from session storage`);
                return parsedKeywords;
              }
            }
          } catch (e) {
            console.warn('Error reading keywords from session storage:', e);
          }
          
          if (Array.isArray(keywords) && keywords.length > 0) {
            console.log(`Using ${keywords.length} keywords from file upload`);
            return keywords;
          }
          
          console.log('Using test keywords as fallback');
          return keywords;
        })()
      });
      
      setClassificationResult(result);
      
      const stats = (result as any).statistics || {};
      
      const keywordsSource = sessionStorage.getItem('latest_keywords') ? 
        'from latest results' : 
        'from uploaded file';
          
      showToast(
        `Classification complete (${keywordsSource}): ${stats.relevant_count || 0} relevant, ` +
        `${stats.undecided_count || 0} undecided, ${stats.outlier_count || 0} outlier keywords`, 
        'success'
      );
      
      if (result.classification_id) {
        try {
          sessionStorage.setItem(
            `workflow_${workflowId}_classification_id`, 
            result.classification_id.toString()
          );
          console.log(`Stored classification ID ${result.classification_id} in sessionStorage`);
        } catch (storageError) {
          console.warn('Could not store classification ID in sessionStorage:', storageError);
        }
      }
    } catch (error) {
      console.error('Error sending direct data to Module 2:', error);
      
      let errorMessage = error instanceof Error ? error.message : 'Error sending data to Module 2';
      
      if (errorMessage.includes('No keywords available')) {
        showToast(
          'No keywords available. Please upload a keywords file or ensure the loaded results contain keywords.', 
          'error'
        );
        
        if (!uploadedFilePath) {
          setTimeout(() => {
            showToast('Try uploading a keywords file in the right panel', 'info');
          }, 3000);
        }
      } else {
        showToast(errorMessage, 'error');
      }
    } finally {
      setIsClassifying(false);
    }
  };

  const closeResultModal = () => {
    setIsResultModalOpen(false);
  };

  const handleCopyResults = () => {
    if (classificationResult) {
      const jsonStr = JSON.stringify(classificationResult, null, 2);
      navigator.clipboard.writeText(jsonStr)
        .then(() => {
          showToast('Results copied to clipboard', 'success');
        })
        .catch(err => {
          console.error('Failed to copy results:', err);
          showToast('Failed to copy results to clipboard', 'error');
        });
    }
  };

  // Expose methods to parent component through ref
  useImperativeHandle(ref, () => ({
    handleExport
  }));

  return (
    <ExportResultsModal
      open={isResultModalOpen}
      onClose={closeResultModal}
      exportedConfig={classificationResult}
      exportError={null}
      exportResult={classificationResult}
      onCopyConfig={handleCopyResults}        
    />
  );
});

export default Module2Integration;
